import { TableSkeleton } from "components/new-stuff/ui/Skeleton"
import { useEffect, useMemo, useRef } from "react"
import { LoadingRegion } from "components/custom/Loader"
import { useLocation, useNavigate } from "react-router-dom"
import { usePostPreview } from "../hooks/report-hooks"
import DashboardWrapper from "components/new-stuff/ui/DashboardWrapper"
import { useAuthStore } from "stores"
import { formatCurrencyNoSymbol } from "utils/currency"
import { ReportDownloadHeading } from "../component/ReportDownloadHeading"
import EnhancedReportTable from "components/new-stuff/report-table/EnhancedReportTable"

const PaybandSummaryPreviewPage = () => {
  const { state } = useLocation()
  const navigate = useNavigate()
  const ref: any = useRef()
  const { user } = useAuthStore()

  useEffect(() => {
    if (!state?.data) {
      navigate(`/dashboard/report/payband-summary`)
    }
  }, [])

  const { getting, data } = usePostPreview(state?.data)

  const allowances = data?.paybandReportSummaryValues
    ?.filter((item: any) => item?.allowance_id > 0 && item?.description)
    ?.sort((a: any, b: any) => a?.allowance_id - b?.allowance_id)
  const statutoryBenefits = data?.paybandReportSummaryValues
    ?.filter((item: any) => item?.benefit_id > 0 && item?.description)
    ?.sort((a: any, b: any) => a?.benefit_id - b?.benefit_id)

  const columns = useMemo(
    () =>
      buildPayScaleColumns({
        data,
        allowances,
        statutoryBenefits,
        type: "payband",
        label: "Payband",
      }),
    [allowances, data, statutoryBenefits]
  )

  return (
    <DashboardWrapper>
      <LoadingRegion
        placeholder={
          <TableSkeleton
            toolbar
            rows={10}
            columns={6}
          />
        }
        spinning={getting}
        keepMounted
      >
        <div className="font-avenir w-[100%]">
          <ReportDownloadHeading
            title={data?.report_download_title || ""}
            ref={null}
            showPdfDownload={false}
          />
          <div
            className="bg-white p-4 flex gap-2 flex-col w-[100%] box-border"
            ref={ref}
          >
            <div className="py-4 border-b border-gray-100">
              <div className="flex justify-between gap-2 items-center">
                <div className="text-lg font-[600] text-[#495057]">
                  Payband Summary Report
                </div>
                {data?.logoUploaded && (
                  <div className="h-[50px] w-[50px]">
                    <img
                      className="float-right object-contain clear-left"
                      src={`${user?.organisationAvatarFileUrl}`}
                      alt="logo"
                      height="100%"
                      width="100%"
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-between gap-3 items-center">
              <div className="text-[#495057]">
                Report created on: {data?.report_date}
              </div>
              <div className="flex justify-end flex-col items-end">
                <div className="text-[#495057]">{user?.userOrganisation}</div>
                <div className="text-[#495057]">
                  {data?.officeAddress?.address_line_1}
                </div>
                <div className="text-[#495057]">
                  {data?.officeAddress?.address_city}&nbsp;
                  {data?.officeAddress?.address_state}&nbsp;
                  {data?.officeAddress?.zip_code}&nbsp;
                  {data?.officeAddress?.country}
                </div>
              </div>
            </div>

            <EnhancedReportTable
              dataSource={data?.paybandReportSummaries || []}
              columns={columns}
              rowKey={(record: any, index?: number) =>
                `payband-${record?.payband_id || index}`
              }
              defaultFreezeBoundaryKey="payband"
              pagination={false}
              scroll={{ y: "calc(100vh - 300px)", x: "max-content" }}
              tableLayout="auto"
              bordered
            />
          </div>
        </div>
      </LoadingRegion>
    </DashboardWrapper>
  )
}

export default PaybandSummaryPreviewPage

export const buildPayScaleColumns = ({
  data,
  allowances = [],
  statutoryBenefits = [],
  taxReliefs = [],
  type,
  label,
}: {
  data: any
  allowances?: any[]
  statutoryBenefits?: any[]
  taxReliefs?: any[]
  type: "payband" | "paygrade"
  label: string
}) => {
  const idKey = `${type}_id`
  const summaryAllowances = data?.[`${type}ReportSummaryAllowances`] || []
  const summaryBenefits = data?.[`${type}ReportSummaryStatutoryBenefits`] || []
  const summaryTaxReliefs = data?.[`${type}ReportSummaryTaxReliefs`] || []

  const columns: any[] = [
    {
      title: label,
      dataIndex: type,
      key: type,
      width: 180,
    },
    {
      title: "Gross (annual)",
      key: "annual_gross",
      render: (_value: any, record: any) =>
        getAmount(record?.currency_symbol, record?.annual_gross),
    },
    {
      title: "Net (monthly)",
      key: "monthly_net",
      render: (_value: any, record: any) =>
        getAmount(record?.currency_symbol, record?.monthly_net),
    },
    {
      title: "Tax (monthly)",
      key: "monthly_tax",
      render: (_value: any, record: any) =>
        getAmount(record?.currency_symbol, record?.monthly_tax),
    },
  ]

  if (data?.alias) {
    columns.push({
      title: "Alias",
      dataIndex: "alias",
      key: "alias",
    })
  }

  const optionalColumns = [
    ["routineAnnualGross", "Regular gross (annual)", "routine_annual_gross"],
    ["monthlyGross", "Gross (monthly)", "monthly_gross"],
    ["routineMonthlyGross", "Regular gross (monthly)", "routine_monthly_gross"],
    ["annualNet", "Net (annual)", "annual_net"],
    ["routineAnnualNet", "Regular net (annual)", "routine_annual_net"],
    ["routineMonthlyNet", "Regular net (monthly)", "routine_monthly_net"],
    ["annualTax", "Tax (annual)", "annual_tax"],
  ]

  optionalColumns.forEach(([flag, title, field]) => {
    if (data?.[flag]) {
      columns.push({
        title,
        key: field,
        render: (_value: any, record: any) =>
          getAmount(record?.currency_symbol, record?.[field] || 0),
      })
    }
  })

  if (allowances.length) {
    columns.push({
      title: "Allowances",
      key: "allowances",
      children: allowances.map((allowance) => ({
        title: allowance?.description,
        key: `allowance_${allowance?.allowance_id}`,
        children: [
          {
            title: "Prorated",
            key: `allowance_${allowance?.allowance_id}_prorated`,
            render: (_value: any, record: any) => {
              const cellData = summaryAllowances.find(
                (item: any) =>
                  item?.[idKey] == record?.[idKey] &&
                  item?.allowance_id == allowance?.allowance_id
              )
              return getAmount(
                record?.currency_symbol,
                cellData?.prorated_amount || 0
              )
            },
          },
          {
            title: "Annual",
            key: `allowance_${allowance?.allowance_id}_annual`,
            render: (_value: any, record: any) => {
              const cellData = summaryAllowances.find(
                (item: any) =>
                  item?.[idKey] == record?.[idKey] &&
                  item?.allowance_id == allowance?.allowance_id
              )
              return getAmount(record?.currency_symbol, cellData?.amount || 0)
            },
          },
        ],
      })),
    })
  }

  if (statutoryBenefits.length) {
    columns.push({
      title: "Statutory benefits",
      key: "statutory_benefits",
      children: statutoryBenefits.map((benefit) => ({
        title: benefit?.description,
        key: `benefit_${benefit?.benefit_id}`,
        children: [
          {
            title: "Monthly deduction",
            key: `benefit_${benefit?.benefit_id}_monthly_deduction`,
            render: (_value: any, record: any) =>
              getAmount(
                record?.currency_symbol,
                getBenefitCell(summaryBenefits, record, benefit, idKey)
                  ?.prorated_deduction_amount || 0
              ),
          },
          {
            title: "Monthly contribution",
            key: `benefit_${benefit?.benefit_id}_monthly_contribution`,
            render: (_value: any, record: any) =>
              getAmount(
                record?.currency_symbol,
                getBenefitCell(summaryBenefits, record, benefit, idKey)
                  ?.prorated_contribution_amount || 0
              ),
          },
          {
            title: "Annual deduction",
            key: `benefit_${benefit?.benefit_id}_annual_deduction`,
            render: (_value: any, record: any) =>
              getAmount(
                record?.currency_symbol,
                getBenefitCell(summaryBenefits, record, benefit, idKey)
                  ?.deduction_amount || 0
              ),
          },
          {
            title: "Annual contribution",
            key: `benefit_${benefit?.benefit_id}_annual_contribution`,
            render: (_value: any, record: any) =>
              getAmount(
                record?.currency_symbol,
                getBenefitCell(summaryBenefits, record, benefit, idKey)
                  ?.contribution_amount || 0
              ),
          },
        ],
      })),
    })
  }

  if (taxReliefs.length) {
    columns.push({
      title: "Tax reliefs",
      key: "tax_reliefs",
      children: taxReliefs.map((taxRelief) => ({
        title: taxRelief?.description,
        key: `tax_relief_${taxRelief?.relief_id}`,
        children: [
          {
            title: "Prorated",
            key: `tax_relief_${taxRelief?.relief_id}_prorated`,
            render: (_value: any, record: any) => {
              const cellData = summaryTaxReliefs.find(
                (item: any) =>
                  item?.[idKey] == record?.[idKey] &&
                  item?.tax_relief_id == taxRelief?.relief_id
              )
              return getAmount(
                record?.currency_symbol,
                cellData?.prorated_amount || 0
              )
            },
          },
          {
            title: "Annual",
            key: `tax_relief_${taxRelief?.relief_id}_annual`,
            render: (_value: any, record: any) => {
              const cellData = summaryTaxReliefs.find(
                (item: any) =>
                  item?.[idKey] == record?.[idKey] &&
                  item?.tax_relief_id == taxRelief?.relief_id
              )
              return getAmount(record?.currency_symbol, cellData?.amount || 0)
            },
          },
        ],
      })),
    })
  }

  return columns
}

const getBenefitCell = (
  items: any[] = [],
  record: any,
  benefit: any,
  idKey: string
) =>
  items.find(
    (item: any) =>
      item?.[idKey] == record?.[idKey] &&
      item?.statutory_benefit_id == benefit?.benefit_id
  )

const getAmount = (symbol: string, amount: number) => {
  return `${symbol}${formatCurrencyNoSymbol(amount)}`
}
